import React, { useEffect, useState } from 'react';
import { ciudadesSelect, EliminarParticipante, estadoCivilSelect, Participante } from '../../models/participantes';
import { Mandatario } from '../../models/mandatario';
import * as Yup from "yup";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import validaRut from '../../../../utils/validaRut';
import { useToast } from '../../../../hooks/useToast';
import { useParticipantes } from '../../hooks/useParticipantes';
import { useMandatarios } from '../../hooks/useMandatarios';
import { format } from 'rut.js';
import Validaciones from '../../../../utils/validaciones';
import { enumTipoParticipanteConValores, StatusCode, TipoFuncionalidad } from '../../../../utils/enums';
import { getListaParticipantes } from '../../services/servicesParticipantes';
import { AxiosResponse } from 'axios';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import InputRut from '../../../../components/inputs/yup/materialui/inputRut';
import InputText from '../../../../components/inputs/yup/materialui/inputText';
import InputFecha from '../../../../components/inputs/yup/materialui/inputFecha';
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import { Card, CardContent, Divider, Grid2, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { ListResponseDto } from '../../../../dtos/ListResponseDto';
import { useDetalleMutuoPermissions } from '../../hooks/useDetalleMutuoPermissions';
import { DetalleMutuoPermissionGuard } from '../../../../components/auth/DetalleMutuoPermissionGuard';

interface IProps {
    dataMandatario: Mandatario | undefined;
    ciudadesSelect: ciudadesSelect[];
    estadoCivilSelect: estadoCivilSelect[];
    idMutuo: number;
    setShowParticipantes: React.Dispatch<React.SetStateAction<boolean>>;
    esParticipanteNuevo: boolean;
    esMutuoNormalyConComplementario: boolean;
    renderizar: () => void;
    volver: () => void;
    handleDelete: (dataDelete: EliminarParticipante) => void
    isComplementario: boolean
    isDesactivado?: boolean
    esVigente?: boolean;
}

const MandatarioForm: React.FC<IProps> = (props) => {

    const { comunasSelect, comunaPorId, setComunasSelect } = useParticipantes();
    const { actualizarMandatario, crearMandatario, mandatarioDefaultValues } = useMandatarios();
    const [creandoNuevoMandatario, setCreandoNuevoMandatario] = useState<boolean>(props.esParticipanteNuevo);
    const [participantesCache, setParticipantesCache] = useState<Participante[]>([]);
    const { permisoCarteraInfo } = useDetalleMutuoPermissions(props.esVigente);

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [toggleRutOriginal, setToggleRutOriginal] = useState<boolean>(false);
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    const schemaMandatario = Yup.object<Mandatario>().shape({

        rut: Yup.string()
            .default('')
            .nonNullable()
            .test({
                name: 'valida-rut',
                exclusive: false,
                params: {},
                message: 'RUT inválido',
                test: (value) => validaRut.rutFormat(value)
            })
            .required('Campo requerido')
            .test({
                name: 'valida-rut-distinto-deudor',
                exclusive: false,
                params: {},
                message: 'RUT debe ser distinto al del resto de los participantes',
                test: async (value, { createError }) => {

                    let participantes: Participante[] = [];

                    if (participantesCache.length == 0) {
                        let resp: AxiosResponse<ListResponseDto<Participante>> = await getListaParticipantes(props.idMutuo);

                        if (resp.status == StatusCode.Ok) {
                            setParticipantesCache(await resp.data.items);
                        }
                        else {
                            return createError({ message: "No fué posible validar el RUT" })
                        }
                    }

                    participantes = participantesCache;

                    let rutIngresado: string = format(value, { dots: false });

                    let deudor: Participante | undefined = participantes?.find(x => x.idTipoParticipante ===
                        enumTipoParticipanteConValores.Deudor || enumTipoParticipanteConValores.DeudorJuridico);
                    if (deudor && rutIngresado === deudor.rut) {
                        return createError({ message: "RUT no permitido, pertenece al deudor del mutuo" });
                    }

                    let conyuge: Participante | undefined = participantes?.find(x => x.idTipoParticipante ===
                        enumTipoParticipanteConValores.Conyuge);
                    if (conyuge && rutIngresado === conyuge.rut)
                        return createError({ message: "RUT no permitido, pertenece al cónyuge del mutuo" });

                    let rutPermitido: boolean = true;
                    participantes?.forEach((participante: Participante) => {

                        if (participante.idTipoParticipante === enumTipoParticipanteConValores.Mandatario) {
                            if (dataFormDeudor.rutOriginal !== rutIngresado && participante.rut === rutIngresado) {
                                rutPermitido = false;
                            }
                        }

                    });

                    if (!rutPermitido)
                        return createError({ message: "RUT no permitido, pertenece a un mandatario ya existente" })

                    rutPermitido = true;
                    participantes?.forEach((participante: Participante) => {

                        if (participante.idTipoParticipante === enumTipoParticipanteConValores.Codeudor) {
                            if (participante.rut === rutIngresado) {
                                rutPermitido = false;
                            }
                        }

                    });

                    if (!rutPermitido)
                        return createError({ message: "RUT no permitido, pertenece a un codeudor" })

                    return true;

                }
            }),

        rutOriginal: Yup.string()
            .nonNullable()
            .required('Campo requerido'),

        apellidopaterno: Yup.string().required('Campo requerido').max(50, "Máximo 50 caractéres")
            .test('valida-apellido.paterno', 'Caractéres inválidos', function (value) {
                return Validaciones.soloLetras(value ?? '')
            }),

        apellidomaterno: Yup.string().required('Campo requerido').max(50, "Máximo 50 caractéres")
            .test('valida-apellido-materno', 'Caractéres inválidos', function (value) {
                return Validaciones.soloLetras(value ?? '')
            }),

        nombres: Yup.string().required('Campo requerido').max(50, "Máximo 50 caractéres")
            .test('valida-nombre', 'Caractéres inválidos', function (value) {
                return Validaciones.soloLetras(value ?? '')
            }),

        direccion: Yup.string().required('Campo requerido').max(100, "Máximo 100 caractéres")
            .test('valida-direccion', 'Caractéres inválidos', function (value) {
                return Validaciones.esLetrasNumeros(value ?? '')
            }),

        ciudadId: Yup.string().required('Campo requerido').max(10, "Máximo 10 caractéres"),

        comunaId: Yup.string().required('Campo requerido').max(10, "Máximo 10 caractéres"),

        fechanacimiento: Yup.date()
            .test({
                name: 'edad-requerida',
                exclusive: false,
                params: {},
                message: 'Campo requerido',
                test: (value: Date | undefined) => {
                    return value !== undefined;
                }
            })
            .test({
                name: 'edad-requerida',
                exclusive: false,
                params: {},
                message: 'Valor inválido',
                test: (value: Date | undefined) => {
                    const hoy = new Date();
                    const fechaLimite = new Date(hoy.getFullYear() - 100, hoy.getMonth(), hoy.getDate());
                    return value !== undefined && value > fechaLimite;
                }
            })
            .test({
                name: 'mayor-edad',
                exclusive: false,
                params: {},
                message: 'El mandatario debe ser mayor de edad',
                test: (value: Date | undefined) => Validaciones.esMayor18Anios(value ?? null)
            }).nonNullable("Campo requerido")
        ,

        nacionalidad: Yup.string().required('Campo requerido').max(50, "Máximo 50 caractéres")
            .test('valida-nacionalidad', 'Caractéres inválidos', function (value) {
                return Validaciones.soloLetras(value ?? '')
            }),

        profesion: Yup.string().required('Campo requerido').max(50, "Máximo 30 caractéres")
            .test('valida-profesion', 'Caractéres inválidos', function (value) {
                return Validaciones.soloLetras(value ?? '')
            }),

        estadocivil: Yup.string().required('Campo requerido')

    });

    const {
        formState: { errors, isDirty, isValid },
        handleSubmit,
        control,
        setValue,
        watch,
        reset,
        trigger
    } = useForm<Mandatario>({
        defaultValues: props.esParticipanteNuevo ? mandatarioDefaultValues : props.dataMandatario,
        mode: "all",
        reValidateMode: "onChange",
        resolver: yupResolver(schemaMandatario)
    })

    const { addToast } = useToast();

    const dataFormDeudor = watch();

    useEffect(() => {

        if (props.dataMandatario?.ciudadId) {
            comunaPorId(props.dataMandatario?.ciudadId);
        }

        if (props.dataMandatario) {
            reset(props.dataMandatario);
        }

        setValue("mutuoid", props.idMutuo);

    }, [props.dataMandatario, reset])

    useEffect(() => {

        setValue("rutOriginal", format(dataFormDeudor.rut, { dots: false }));

    }, [toggleRutOriginal])

    useEffect(() => {

        if (creandoNuevoMandatario == true) {
            trigger();
        }

    }, [creandoNuevoMandatario]);

    const handleCiudadComuna = async (id: string | null) => {

        setValue('comunaId', '');
        setComunasSelect([]);

        trigger("comunaId");

        if (id) {
            await comunaPorId(id, false);
        }
    }

    const handleCancelarEdicionClick = () => {

        if (isDirty) {
            setModalConfirmacion({
                show: true,
                title: "Hay cambios sin guardar",
                message: "¿Desea abandonar la edición?",
                metodo: () => {
                    setIsEditing(false);
                    reset();
                },
            });
        }
        else {
            setIsEditing(false);
            reset();
        }

    }

    const onSubmit = (data: Mandatario) => {

        data.rut = format(data.rut, { dots: false });

        setIsSubmitting(true);

        actualizarMandatario(data)
            .then(() => {
                addToast({
                    mostrar: true,
                    titulo: 'Confirmación',
                    tipo: 'Success',
                    mensaje: 'Datos modificados correctamente'
                });

                setIsEditing(false);
                setIsSubmitting(false);

                if (format(dataFormDeudor.rut, { dots: false }) != dataFormDeudor.rutOriginal) {
                    setToggleRutOriginal(!toggleRutOriginal);
                }

                props.renderizar();

                reset(dataFormDeudor);
            })
            .catch(() => {
                setIsSubmitting(false);
            });
    };

    const onSubmitNuevo = (data: Mandatario) => {

        data.rut = format(data.rut, { dots: false });

        setIsSubmitting(true);

        crearMandatario(data)
            .then(() => {
                addToast({
                    mostrar: true,
                    titulo: 'Confirmación',
                    tipo: 'Success',
                    mensaje: 'Mandatario creado correctamente'
                });

                setIsSubmitting(false);
                setCreandoNuevoMandatario(false);

                if (format(dataFormDeudor.rut, { dots: false }) != dataFormDeudor.rutOriginal) {
                    setToggleRutOriginal(!toggleRutOriginal);
                }

                props.renderizar();

                reset(dataFormDeudor);
            })
            .catch(() => {
                setIsSubmitting(false);
            });
    };

    const handleCancelarCreacionClick = () => {

        if (isDirty) {
            setModalConfirmacion({
                show: true,
                title: "No ha guardado los datos",
                message: "¿Desea abandonar el ingreso?",
                metodo: () => {
                    props.setShowParticipantes(true);
                    setCreandoNuevoMandatario(false);
                    props.volver();
                    reset();
                },
            });
        }
        else {
            props.setShowParticipantes(true);
            setCreandoNuevoMandatario(false);
            props.volver();
            reset();
        }

    }

    const eliminarMandatario = async () => {
        const data: EliminarParticipante = {
            idTipoParticipante: enumTipoParticipanteConValores.Mandatario,
            rut: dataFormDeudor.rut ?? props.dataMandatario?.rut,
            nombre: `${dataFormDeudor.nombres} ${dataFormDeudor.apellidopaterno} ${dataFormDeudor.apellidomaterno}`
        }
        props.handleDelete(data);
    }

    const isReadonlyWithPermission = () => {
        // Si NO es vigente (Bandeja), NO valida permisos
        if (!props.esVigente) return !isEditing && !creandoNuevoMandatario;
        // Si ES vigente (Cartera), valida permiso
        return !isEditing && !creandoNuevoMandatario && permisoCarteraInfo;
    }
    const isReadonlyWithPermissionRut = () => {
        // Si NO es vigente (Bandeja), NO valida permisos
        if (!props.esVigente) return !isEditing && !creandoNuevoMandatario;
        //si es vigente (Cartera), no es editable
        return true;
    }


    return (
        <>

            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({
                        ...modalConfirmacion,
                        show: false,
                    })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />

            <form onSubmit={handleSubmit(onSubmit)}>

                {!isEditing && !creandoNuevoMandatario && <>

                    <Grid2 container alignItems="center" spacing={2}>

                        <Grid2 size={2}>
                            <h3>Mandatario</h3>
                        </Grid2>


                        <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                            {!props.isComplementario && (
                                <>
                                    <DetalleMutuoPermissionGuard
                                        esVigente={props.esVigente}
                                        permissions={[TipoFuncionalidad.Bandeja_editar]}>
                                        <MButton
                                            variant='contained'
                                            color='error'
                                            startIcon={<DeleteForeverIcon />}
                                            disabled={props.isDesactivado}
                                            onClick={() => { eliminarMandatario() }}
                                        >
                                            Eliminar
                                        </MButton>
                                    </DetalleMutuoPermissionGuard>
                                    <DetalleMutuoPermissionGuard
                                        esVigente={props.esVigente}
                                        permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info, TipoFuncionalidad.Cartera_editar_contacto]}>

                                        <MButton
                                            variant="contained"
                                            startIcon={<EditIcon />}
                                            disabled={props.isDesactivado}
                                            onClick={() => { setIsEditing(true); trigger(); }}>
                                            Editar
                                        </MButton>
                                    </DetalleMutuoPermissionGuard>
                                </>
                            )}

                            <MButton
                                variant="contained"
                                startIcon={<PeopleAltOutlinedIcon />}
                                onClick={() => { props.setShowParticipantes(true); setIsEditing(false); props.volver(); }}
                            >
                                Listado participantes
                            </MButton>
                        </Grid2>

                    </Grid2>

                </>
                }

                {isEditing && !creandoNuevoMandatario && <>


                    <Grid2 container alignItems="center" spacing={2}>

                        <Grid2 size={2}>
                            <h3>Mandatario</h3>
                        </Grid2>

                        <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                            <MButton
                                variant='contained'
                                color='primary'
                                startIcon={<SaveAltIcon />}
                                disabled={!(isDirty && isValid) || isSubmitting}
                                onClick={handleSubmit(onSubmit)}>
                                Guardar
                            </MButton>

                            <MButton
                                variant='contained'
                                color={'inherit'}
                                startIcon={<CloseIcon />}
                                onClick={() => { handleCancelarEdicionClick(); }}>
                                Cancelar
                            </MButton>

                        </Grid2>

                    </Grid2>
                </>
                }

                {creandoNuevoMandatario && <>

                    <Grid2 container alignItems="center" spacing={2} sx={{ mb: 2, mt: 2 }}>

                        <Grid2 size={2}>
                            <h3>Mandatario</h3>
                        </Grid2>

                        <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>

                            <MButton
                                variant='contained'
                                color='primary'
                                disabled={!(isDirty && isValid) || isSubmitting}
                                startIcon={<SaveAltIcon />}
                                onClick={handleSubmit(onSubmitNuevo)}>
                                Guardar
                            </MButton>

                            <MButton
                                variant='contained'
                                color={'inherit'}
                                startIcon={<CloseIcon />}
                                onClick={() => { handleCancelarCreacionClick(); }}>
                                Cancelar
                            </MButton>

                        </Grid2>
                    </Grid2>
                </>
                }

                <Divider sx={{ my: 2 }} />
                <Grid2 sx={{ mt: 2, mb: 3 }}>

                    <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Datos Personales
                            </Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputRut
                                        label='RUT'
                                        name="rut"
                                        control={control}
                                        error={errors.rut}
                                        readonly={isReadonlyWithPermissionRut()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label='Apellido paterno'
                                        name="apellidopaterno"
                                        control={control}
                                        error={errors.apellidopaterno}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label='Apellido materno'
                                        name="apellidomaterno"
                                        control={control}
                                        error={errors.apellidomaterno}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label='Nombres'
                                        name="nombres"
                                        control={control}
                                        error={errors.nombres}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputFecha
                                        label='Fecha nacimiento'
                                        name="fechanacimiento"
                                        control={control}
                                        fecha={dataFormDeudor?.fechanacimiento ?? undefined}
                                        error={errors.fechanacimiento}
                                        esReadOnly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label='Profesión o actividad'
                                        name="profesion"
                                        control={control}
                                        error={errors.profesion}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label='Estado civil y régimen matrimonial'
                                        name={'estadocivil'}
                                        control={control}
                                        options={props.estadoCivilSelect}
                                        error={errors.estadocivil}
                                        disabled={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label='Nacionalidad'
                                        name={'nacionalidad'}
                                        control={control}
                                        error={errors.nacionalidad}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>

                            </Grid2>
                        </CardContent>
                    </Card>


                    <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
                        <CardContent>
                            <Typography className='card-title' variant="h6">Dirección particular</Typography>

                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label='Calle'
                                        name="direccion"
                                        control={control}
                                        error={errors.direccion}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label='Ciudad'
                                        name={'ciudadId'}
                                        control={control}
                                        options={props.ciudadesSelect}
                                        error={errors.ciudadId}
                                        disabled={isReadonlyWithPermission()}
                                        onChange={(value) => handleCiudadComuna(value ?? '')}
                                    ></InputSelect>
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label='Comuna'
                                        name={'comunaId'}
                                        control={control}
                                        options={comunasSelect}
                                        error={errors.comunaId}
                                        disabled={isReadonlyWithPermission()}
                                    ></InputSelect>
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>
                </Grid2>
            </form>
        </>
    );
};

export default MandatarioForm;